import { useMemo } from "react";
import { 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  CartesianGrid
} from "recharts";
import type { AffiliateActivityDataPoint } from "../types";
import {
  type DateRangeValue,
  isCustomRange,
} from "@/components/ui/date-range-selector";
import { format } from "date-fns";

type AffiliateActivityChartProps = {
  data: AffiliateActivityDataPoint[];
  isLoading: boolean;
  dateRange: DateRangeValue;
};

// Helper function to get period label
function getPeriodLabel(dateRange: DateRangeValue): string {
  if (isCustomRange(dateRange)) {
    return `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d, yyyy")}`;
  }
  
  const labels: Record<string, string> = {
    today: "Today",
    yesterday: "Yesterday",
    this_week: "This Week",
    last_week: "Last Week",
    this_month: "This Month",
    last_month: "Last Month",
    last_7_days: "Last 7 Days",
    last_30_days: "Last 30 Days",
    last_90_days: "Last 90 Days",
    ytd: "Year to Date",
    all: "All Time",
  };
  return labels[dateRange] || "Last 30 Days";
}

export const AffiliateActivityChart = ({ data, isLoading, dateRange }: AffiliateActivityChartProps) => {
  const periodLabel = getPeriodLabel(dateRange);
  
  const chartData = useMemo(() => {
    return data.map((d) => ({
      ...d,
      dateLabel: new Date(d.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    }));
  }, [data]);

  const totals = useMemo(() => {
    return data.reduce(
      (acc, d) => ({
        clicks: acc.clicks + d.clicks,
        referrals: acc.referrals + d.referrals,
      }),
      { clicks: 0, referrals: 0 }
    );
  }, [data]);

  const avgConversionRate = useMemo(() => {
    if (totals.clicks === 0) return 0;
    return (totals.referrals / totals.clicks) * 100;
  }, [totals]);

  if (isLoading) {
    return (
      <div className="rounded-xl bg-card p-4 md:p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="animate-pulse">
          <div className="h-5 w-32 bg-muted rounded mb-4" />
          <div className="h-[200px] bg-muted/50 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-card p-4 md:p-6 border border-border/50 shadow-sm dark:shadow-none h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold">Activity Over Time</h3>
          <p className="text-xs text-muted-foreground">{periodLabel}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Avg. Conversion</p>
          <p className="text-lg font-bold text-pink-500">
            {avgConversionRate.toFixed(1)}%
          </p>
        </div>
      </div>

      <div className="h-[220px] md:h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(var(--border))" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              tickLine={false}
              axisLine={false}
              stroke="#9ca3af"
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              tickLine={false}
              axisLine={false}
              stroke="#9ca3af"
              width={40}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              tickLine={false}
              axisLine={false}
              stroke="#9ca3af"
              tickFormatter={(value) => `${value}%`}
              width={45}
              domain={[0, 100]}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                    <p className="text-xs font-medium mb-2">{label}</p>
                    {payload.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2 text-xs">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-muted-foreground">
                          {entry.name === "clicks" 
                            ? "Clicks" 
                            : entry.name === "referrals" 
                            ? "Referrals" 
                            : "Conversion Rate"}:
                        </span>
                        <span className="font-medium">
                          {entry.name === "conversionRate" 
                            ? `${(entry.value as number).toFixed(1)}%`
                            : entry.value}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            <Bar
              yAxisId="left"
              dataKey="clicks"
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
              opacity={0.8}
            />
            <Bar
              yAxisId="left"
              dataKey="referrals"
              fill="#8b5cf6"
              radius={[4, 4, 0, 0]}
              opacity={0.8}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="conversionRate"
              stroke="#ec4899"
              strokeWidth={2}
              dot={{ fill: "#ec4899", strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5, strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 md:gap-6 mt-4 pt-4 border-t border-border/50 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500" />
          <span className="text-xs text-muted-foreground">Clicks ({totals.clicks})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-violet-500" />
          <span className="text-xs text-muted-foreground">Referrals ({totals.referrals})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-pink-500" />
          <span className="text-xs text-muted-foreground">Conversion Rate</span>
        </div>
      </div>
    </div>
  );
};

